/* ============================================================================
   cart-mrp.js — real MRP on the cart items we post to the order APIs.

   WHY THIS EXISTS
   Shopify cart line items carry no compare-at price. `item.original_price` is
   the selling price *before discounts* (Shopify's own definition), NOT the MRP,
   and `/cart.js` + `{{ cart.items | json }}` expose no compare-at field at all.
   The order backend derives the order's `compare_at_price` line-item property
   from `item.original_price` (payments/functions/web.checkout.helper.js:209),
   so every web order recorded MRP == selling price — e.g. ST342084712507,
   variant 42183608172782: charged 6783.00, MRP written 6783.00, when the
   variant's real compare-at was 7850.00.

   handle-cart DOES return the genuine Storefront compare-at price per line
   (Cart/Functions/graphQL.js:889), and the order payloads already carry that
   same object as `lineItems`. So we copy it onto `original_price` before the
   payload goes out, and the backend writes the real MRP.

   UNITS: handle-cart amounts are RUPEES as a string ("7850.0"); cart line items
   are PAISE (678300). Everything written here is converted to paise.

   Do not "restore" original_price to the Shopify meaning without also changing
   the backend — the two are a pair.
   ============================================================================ */
(function () {
  'use strict';

  /**
   * Bare numeric variant id from a Storefront gid.
   * @param {string} gid e.g. "gid://shopify/ProductVariant/42183608172782"
   */
  function variantIdFromGid(gid) {
    return typeof gid === 'string' ? gid.split('/').pop() : '';
  }

  /** The handle-cart checkout object, from the caller or localStorage. */
  function resolveCheckout(handleCartCheckout) {
    if (handleCartCheckout && handleCartCheckout.lineItems) return handleCartCheckout;
    try {
      var stored = JSON.parse(localStorage.getItem('handle-cart'));
      return (stored && stored.checkoutDiscountCodeApplyV2 && stored.checkoutDiscountCodeApplyV2.checkout) || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Variant id -> compare-at price in PAISE, from a handle-cart response.
   * Lines without a compare-at price (Shopify returns null) are left out.
   * @returns {Object}
   */
  function mrpPaiseByVariantId(handleCartCheckout) {
    var checkout = resolveCheckout(handleCartCheckout);
    var nodes = (checkout && checkout.lineItems && checkout.lineItems.nodes) || [];

    return nodes.reduce(function (map, node) {
      var variant = node && node.variant;
      var rupees = variant && variant.compareAtPrice && parseFloat(variant.compareAtPrice.amount);
      var id = variantIdFromGid(variant && variant.id);
      if (id && isFinite(rupees) && rupees > 0) {
        map[id] = Math.round(rupees * 100);
      }
      return map;
    }, {});
  }

  /**
   * Returns the cart items with `original_price` set to the real MRP in paise.
   *
   * Copies rather than mutates: these item objects are shared with the live cart
   * (window.shopifyCart / the sticky drawer), and writing onto them would change
   * what every other reader sees.
   *
   * A line keeps its own original_price when handle-cart is missing/stale, the
   * variant has no compare-at price, or the MRP is not above what is charged —
   * i.e. exactly the behaviour before this helper, so nothing regresses.
   *
   * @param {Array} items - Shopify cart line items (paise).
   * @param {Object} [handleCartCheckout] - handleCart.checkoutDiscountCodeApplyV2.checkout.
   * @returns {Array}
   */
  function getCartItemsWithMrp(items, handleCartCheckout) {
    if (!Array.isArray(items) || items.length === 0) return items || [];

    var mrpByVariant = mrpPaiseByVariantId(handleCartCheckout);
    if (Object.keys(mrpByVariant).length === 0) return items;

    return items.map(function (item) {
      var mrp = mrpByVariant[String(item && item.variant_id)];
      if (!mrp || !(mrp > item.price)) return item;
      return Object.assign({}, item, { original_price: mrp });
    });
  }

  window.getCartItemsWithMrp = getCartItemsWithMrp;
})();
